import React from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import { EmailAdminSettings, updateEmailSettings } from '../../../api/adminSettingsApi';

interface EmailConfigurationProps {
  initialValues: EmailAdminSettings | null;
  loading: boolean;
  onRefresh: () => void;
  onRequestSecretUpdate: (title: string, fieldLabel: string) => void;
}

export const EmailConfiguration: React.FC<EmailConfigurationProps> = ({ initialValues, loading, onRefresh, onRequestSecretUpdate }) => {
  const [formData, setFormData] = React.useState<EmailAdminSettings>({ tenantId: '', clientId: '', from: '' });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (initialValues) {
      setFormData({
        tenantId: initialValues.tenantId || '',
        clientId: initialValues.clientId || '',
        from: initialValues.from || '',
        clientSecret: initialValues.clientSecret || '',
      });
    }
  }, [initialValues]);

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await updateEmailSettings(formData);
      toast.success('Email configuration updated');
      onRefresh();
    } catch {
      toast.error('Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-strivre-lightnavy bg-slate-50/40 p-8 text-slate-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading email settings...
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="space-y-5 rounded-2xl border border-strivre-lightnavy bg-white p-6 shadow-sm text-left">
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Tenant ID</label>
        <input
          value={formData.tenantId}
          onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
          className="w-full rounded-xl border border-strivre-lightnavy text-slate-900 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Client ID</label>
        <input
          value={formData.clientId}
          onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
          className="w-full rounded-xl border border-strivre-lightnavy text-slate-900 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">From Address</label>
        <input
          value={formData.from}
          onChange={(e) => setFormData({ ...formData, from: e.target.value })}
          className="w-full rounded-xl border border-strivre-lightnavy text-slate-900 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
      </div>

      <div className="rounded-2xl border border-strivre-lightnavy bg-slate-50/40 p-5">
        <label className="text-sm font-semibold text-slate-700">Client Secret</label>
        <div className="mt-2 rounded-xl border border-strivre-lightnavy bg-white px-4 py-3 text-sm text-slate-600">
          {initialValues?.clientSecret ? 'Configured ✅' : 'Not Configured ❌'}
        </div>
        <button type="button" onClick={() => onRequestSecretUpdate('Update Client Secret', 'Client Secret')} className="mt-4 rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-sm font-semibold text-slate-700">
          Update Client Secret
        </button>
      </div>

      <div className="flex justify-end">
        <button type="submit" disabled={saving} className="flex items-center gap-2 rounded-2xl bg-strivre-burgundy px-6 py-3 text-sm font-semibold text-white shadow-sm">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </form>
  );
};
